/**
 * ENG-10194: does this memory's TIER outlive the content the agent is putting
 * in it?
 *
 * ── THE FAILURE ──
 *
 * `memory_save` offers five types. Four are durable; `daily` carries a 48-hour
 * TTL that the caller is never shown. The tool's own `type` description said
 * only `daily (ephemeral)`, so an agent choosing it was choosing a deletion
 * deadline it had not been told about — and nothing anywhere compared that
 * deadline against how long the content needed to live.
 *
 * The consequences are measured, not hypothetical. ENG-9789's own migration
 * header records FOUR agents (claire, penny x2, robin) independently calling
 * `memory_save(type:'daily')` to store standing behavioural conventions, and
 * says why: "They converged on the same wrong choice because there was no right
 * one." ENG-9355 then made the TTL bite — on 2026-08-24, **933 rows across 58
 * agents** expired in a single day against a background of 1–49/day either
 * side. Triage of the 195 descriptively-named survivors of that day
 * (`docs/research/eng-10194-prod-results.md` §3) found the real losses were not
 * mis-filed policy documents but **state a recurring task needed on its next
 * run**:
 *
 *   - `scout` / `urgent-email-reported` — a deduplication ledger: "Message ids
 *     already surfaced to Brad by the Hourly Urgent Email Check (skip these on
 *     future runs to avoid duplicate reports)." When it expired the suppression
 *     list emptied, silently, with the task still running.
 *   - `sophie` / "check DAILY whether business class is available Phuket →
 *     Melbourne, and advise him when it opens" — an open-ended commitment to a
 *     person, in a 48-hour tier.
 *
 * Every one of those failures is invisible. An expired memory returns nothing;
 * there is no error, no log, and no difference between "the ledger is empty
 * because nothing matched" and "the ledger is empty because it was deleted".
 *
 * ── WHAT THIS MODULE DOES, AND IN WHICH ORDER ──
 *
 * Three checks, deliberately of DIFFERENT kinds, because one of them is exact
 * and two are guesses and the caller should be able to tell which is which.
 *
 *   1. {@link describeExpiringTier} — UNCONDITIONAL and exact. Every save into
 *      an expiring tier states the actual deletion time and names the durable
 *      alternatives. No classification, so no false negatives: an agent can no
 *      longer pick this tier without being told what it costs. This is the
 *      deliverable; 2 and 3 are enhancements on top of it.
 *
 *   2. {@link isCarriedForward} — STRUCTURAL, and the high-precision one. A
 *      `daily` row that is being UPDATED and is already older than the tier's
 *      own TTL has survived only because each re-save reset its clock. That is
 *      arithmetic, not interpretation: the tier would have destroyed it, the
 *      agent kept it alive by hand, and therefore the agent is using an
 *      expiring tier as cross-run storage. This is exactly `scout`'s ledger —
 *      re-saved every hour for weeks — and it cannot fire on a journal entry,
 *      because each day's journal gets a NEW name and so is an insert.
 *
 *   3. {@link findStandingMarkers} — LEXICAL, and the low-precision one. It
 *      looks for the phrases a standing instruction is actually written in.
 *      Kept because check 2 cannot see a rule on its FIRST write, which is the
 *      write that matters most, and `sophie`'s watch was exactly that shape.
 *
 * ── WHY THIS FLAGS AND NEVER REFUSES ──
 *
 * A refusal loses the write. The agent has content in hand and one call to make
 * it durable; answering "no" leaves it holding something with nowhere to put it,
 * which is how it ended up in `daily` the first time. Every path here returns
 * the SAME successful save plus a sentence naming the better home. The sentence
 * routes — to `rule_add` for a standing instruction, to a durable type for
 * cross-run state — rather than merely disapproving, because ENG-9789's finding
 * was that agents reach for the wrong mechanism when no right one is named.
 *
 * ── THE PRECISION TRADE, STATED RATHER THAN HIDDEN ──
 *
 * Check 3 will fire on prose that merely CONTAINS a marker — a journal entry
 * reporting that someone always wants the summary first is not itself a
 * standing rule. That is accepted deliberately, and the reasoning is the
 * asymmetry of the two errors: a false positive costs one extra sentence in a
 * tool reply that the agent can ignore, and a false negative costs the whole
 * failure again, silently, for as long as the content was meant to live. The
 * unconditional check 1 is what stops this being an alarm nobody reads — the
 * routing sentence is an addition to a message that was already going to be
 * sent, not a new interruption.
 */

/**
 * How long a `daily` memory lives after its last write.
 *
 * The single definition of the number. `memory_save` computes `expires_at` from
 * this rather than from its own literal, so the deadline stated in the reply and
 * the deadline written to the row cannot disagree — which is the specific way a
 * "we tell the agent when it expires" fix would otherwise rot.
 *
 * NOT the only 48 in the system: the manager stamps its own TTL on every disk
 * memory file it syncs (`host-runtime.ts` ENG-9355 comment). That one is
 * host-side and is deliberately left alone here; see the disk-path gap recorded
 * in `docs/research/eng-10194-finding-memory-md-not-synced.md`.
 */
export const DAILY_MEMORY_TTL_HOURS = 48;

export const DAILY_MEMORY_TTL_MS = DAILY_MEMORY_TTL_HOURS * 60 * 60 * 1000;

/** The memory types that carry a deletion deadline. */
export const EXPIRING_MEMORY_TYPES = ['daily'] as const;

/** The memory types that do not. Named in the reply, so an agent has somewhere to go. */
export const DURABLE_MEMORY_TYPES = ['user', 'feedback', 'project', 'reference'] as const;

export function isExpiringMemoryType(type: string): boolean {
  return (EXPIRING_MEMORY_TYPES as readonly string[]).includes(type);
}

/**
 * Phrases a standing instruction is written in.
 *
 * DERIVED FROM THE ROWS, not from intuition. The first three groups are lifted
 * from the prod failures triaged in `eng-10194-prod-results.md` §3; the fourth
 * is the set `rule_add`'s own tool description already teaches agents to
 * recognise ("always…", "every time…", "from now on…"), so an agent that has
 * read that description and written the phrase anyway is pointed at the tool it
 * was already told about.
 *
 * All matching is case-insensitive and, for the single words, word-bounded —
 * without the boundary `never` matches "whenever" and the check would fire on
 * most English.
 */
export const STANDING_CONTENT_MARKERS: readonly string[] = [
  // Cross-run continuity — scout's deduplication ledger.
  'future runs',
  'on future runs',
  'next run',
  'subsequent runs',
  'skip these',
  'already surfaced',
  'already reported',
  'already sent',
  'avoid duplicate',
  'do not repeat',
  // Open-ended commitments — sophie's flight watch.
  'until further notice',
  'until told otherwise',
  'unless told otherwise',
  'keep checking',
  'keep watching',
  'check daily',
  'check every',
  'ongoing',
  'permanently',
  // Self-described standing content.
  'standing instruction',
  'standing rule',
  'standing order',
  // The phrases rule_add already names.
  'from now on',
  'going forward',
  'every time',
  'each time',
  'always',
  'never',
];

/** Markers that must match on a word boundary rather than as a substring. */
const WORD_BOUNDED = new Set(['always', 'never', 'ongoing', 'permanently']);

function markerMatches(haystack: string, marker: string): boolean {
  if (!WORD_BOUNDED.has(marker)) return haystack.includes(marker);
  // Escaping is unnecessary for this set (letters only) but the guard is here so
  // adding a marker with a regex metacharacter cannot quietly change the
  // pattern's meaning.
  const escaped = marker.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`\\b${escaped}\\b`).test(haystack);
}

/**
 * Which standing-instruction markers does this memory carry?
 *
 * Returns the matched markers rather than a boolean so the caller can say WHICH
 * phrase it reacted to. "This looks like a standing instruction" invites the
 * agent to argue with it; "you wrote 'from now on'" does not, and it also makes
 * a wrong match obviously wrong rather than mysteriously wrong.
 *
 * Name and content are searched together: `sophie`'s watch put the durable half
 * of its meaning in the name ("HKT-MEL biz daily watch"), and a check that read
 * only the body would have missed it.
 */
export function findStandingMarkers(name: string, content: string): string[] {
  const haystack = `${name}\n${content}`.toLowerCase();
  return STANDING_CONTENT_MARKERS.filter((m) => markerMatches(haystack, m));
}

/**
 * Is this save carrying an expiring row FORWARD past its own TTL?
 *
 * True when an existing row in an expiring tier is being re-saved and was
 * created longer ago than the tier keeps anything. The row is only still there
 * because the previous save reset its clock, so this is a statement about what
 * has already happened rather than a prediction: the tier's guarantee has
 * already been exceeded, by hand, at least once.
 *
 * `createdAt` unparseable or absent → false. A missing timestamp is not
 * evidence of carrying-forward, and inventing one would make the exact check
 * into a third guess.
 */
export function isCarriedForward(args: {
  type: string;
  isUpdate: boolean;
  createdAt: string | null | undefined;
  now?: number;
}): boolean {
  if (!args.isUpdate) return false;
  if (!isExpiringMemoryType(args.type)) return false;
  if (!args.createdAt) return false;
  const created = Date.parse(args.createdAt);
  if (Number.isNaN(created)) return false;
  const now = args.now ?? Date.now();
  return now - created > DAILY_MEMORY_TTL_MS;
}

/** Whole-days elapsed since `createdAt`, or null when it cannot be read. */
export function ageInDays(createdAt: string | null | undefined, now = Date.now()): number | null {
  if (!createdAt) return null;
  const created = Date.parse(createdAt);
  if (Number.isNaN(created)) return null;
  return Math.max(0, Math.floor((now - created) / (24 * 60 * 60 * 1000)));
}

export interface MemoryLifetimeNoticeArgs {
  type: string;
  name: string;
  content: string;
  /** The `expires_at` actually written to the row, so the two cannot disagree. */
  expiresAt: string | null;
  isUpdate: boolean;
  createdAt?: string | null;
  now?: number;
}

/**
 * The sentence(s) to append to a `memory_save` reply, or '' when the tier
 * carries no deadline.
 *
 * At most ONE routing hint is added, and the structural check wins when both
 * fire. Two hints in one reply would be arguing with itself: "this is cross-run
 * state" and "this is a standing rule" point at different destinations, and an
 * agent handed both has been told to do two things about one memory.
 */
export function describeMemoryLifetime(args: MemoryLifetimeNoticeArgs): string {
  if (!isExpiringMemoryType(args.type)) return '';

  const deadline = args.expiresAt
    ? ` It will be deleted at ${args.expiresAt}`
    : ` It carries a ${DAILY_MEMORY_TTL_HOURS}-hour deletion deadline`;
  const durables = DURABLE_MEMORY_TYPES.join(', ');
  const base =
    ` Note: "${args.type}" is the only expiring memory type.${deadline} —` +
    ` ${DAILY_MEMORY_TTL_HOURS} hours after this save — and nothing will tell you when it goes.` +
    ` If this has to outlive that, save it again as one of: ${durables}.`;

  if (isCarriedForward({ type: args.type, isUpdate: args.isUpdate, createdAt: args.createdAt, now: args.now })) {
    const days = ageInDays(args.createdAt, args.now);
    const age = days === null ? 'longer than that' : `${days} day${days === 1 ? '' : 's'}`;
    return (
      `${base}` +
      ` You first saved this ${age} ago and it has only survived because each re-save` +
      ` restarts the clock — so this is state you are carrying between runs, not a note about one day.` +
      ` Move it to a durable type; if you ever miss a re-save it is gone, and nothing reports that.`
    );
  }

  const markers = findStandingMarkers(args.name, args.content);
  if (markers.length > 0) {
    return (
      `${base}` +
      ` It also reads like a standing instruction ("${markers[0]}"). If someone asked you to do this` +
      ` from now on, it belongs in a standing rule (rule_add) so it applies on every turn,` +
      ` or in a durable memory type — not in one that deletes itself.`
    );
  }

  return base;
}
